import type { ReactNode } from "react";

/**
 * Lucide glyphs for the member Home layout. Each strokes with `currentColor`,
 * so a use site tints it through its text color, and each is aria-hidden.
 */

interface IconProps {
  /** Rendered square size in px (the viewBox scales). */
  size: number;
  className?: string | undefined;
}

const stroke = { stroke: "currentColor", strokeWidth: 2 } as const;
const roundStroke = { ...stroke, strokeLinecap: "round" } as const;

function Glyph({
  size,
  className,
  viewBox,
  children,
}: IconProps & { viewBox: string; children: ReactNode }): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      fill="none"
      aria-hidden="true"
      className={className}
    >
      {children}
    </svg>
  );
}

export function HomeIcon(props: IconProps): ReactNode {
  return (
    <Glyph {...props} viewBox="0 0 20 20">
      <path
        d="M12.5 17.4993V10.8327C12.5 10.6117 12.4122 10.3997 12.2559 10.2434C12.0996 10.0871 11.8877 9.99933 11.6667 9.99933H8.33333C8.11232 9.99933 7.90036 10.0871 7.74408 10.2434C7.5878 10.3997 7.5 10.6117 7.5 10.8327V17.4993M2.5 8.33307C2.49994 8.09062 2.55278 7.85109 2.65482 7.63116C2.75687 7.41124 2.90566 7.21623 3.09083 7.05973L8.92417 2.05973C9.22499 1.80549 9.60613 1.666 10 1.666C10.3939 1.666 10.775 1.80549 11.0758 2.05973L16.9092 7.05973C17.0943 7.21623 17.2431 7.41124 17.3452 7.63116C17.4472 7.85109 17.5001 8.09062 17.5 8.33307V15.8331C17.5 16.2751 17.3244 16.699 17.0118 17.0116C16.6993 17.3241 16.2754 17.4997 15.8333 17.4997H4.16667C3.72464 17.4997 3.30072 17.3241 2.98816 17.0116C2.67559 16.699 2.5 16.2751 2.5 15.8331V8.33307Z"
        {...roundStroke}
      />
    </Glyph>
  );
}

export function HeartHandshakeIcon(props: IconProps): ReactNode {
  return (
    <Glyph {...props} viewBox="0 0 20 20">
      <path
        d="M10.3409 4.85385L7.98817 7.15543C7.6757 7.46796 7.50016 7.89179 7.50016 8.33371C7.50016 8.77563 7.6757 9.19946 7.98817 9.51199C8.30074 9.82443 8.72462 9.99995 9.1666 9.99995C9.60858 9.99995 10.0325 9.82443 10.345 9.51199L11.7701 8.08705C11.9567 7.90048 12.1781 7.75247 12.4219 7.65149C12.6656 7.55051 12.9268 7.49854 13.1907 7.49854C13.4545 7.49854 13.7158 7.55051 13.9595 7.65149C14.2032 7.75247 14.4247 7.90048 14.6112 8.08705L16.1788 9.65532C16.4913 9.96786 16.6668 10.3917 16.6668 10.8336C16.6668 11.2755 16.4913 11.6994 16.1788 12.0119C17.5006 10.6903 18.334 9.58366 18.334 7.91706C18.334 6.98977 18.0526 6.08429 17.5272 5.32022C17.0017 4.55615 16.2567 3.96944 15.3908 3.63757C14.5248 3.3057 13.5785 3.24429 12.6769 3.46145C11.7753 3.67861 10.9607 4.16412 10.3409 4.85385ZM16.1788 12.0119C16.0145 12.1763 15.8193 12.3067 15.6045 12.3956C15.3897 12.4846 15.1595 12.5304 14.9271 12.5304C14.6946 12.5304 14.4644 12.4846 14.2496 12.3956C14.0348 12.3067 13.8397 12.1763 13.6753 12.0119C13.8533 12.1728 13.9967 12.3682 14.0968 12.5862C14.1969 12.8043 14.2516 13.0404 14.2576 13.2802C14.2637 13.5201 14.2209 13.7586 14.1318 13.9814C14.0428 14.2042 13.9094 14.4066 13.7397 14.5762C13.5701 14.7459 13.3677 14.8792 13.1449 14.9683C12.922 15.0573 12.6835 15.1001 12.4436 15.0941C12.2037 15.088 11.9676 15.0333 11.7495 14.9332C11.5315 14.8331 11.336 14.6897 11.1751 14.5118C11.3396 14.6756 11.4702 14.8703 11.5594 15.0847C11.6486 15.299 11.6947 15.5289 11.695 15.761C11.6953 15.9932 11.6499 16.2232 11.5612 16.4378C11.4726 16.6524 11.3425 16.8474 11.1784 17.0117C11.0201 17.1701 10.8314 17.2949 10.6237 17.3788C10.416 17.4626 10.1935 17.5038 9.96954 17.4997C9.74558 17.4957 9.52473 17.4465 9.32019 17.3552C9.11565 17.2639 8.93161 17.1324 8.77907 16.9684L4.1662 12.5002C2.9161 11.2503 1.666 9.83365 1.666 7.91706C1.66619 6.98984 1.94764 6.08448 2.4732 5.32054C2.99875 4.5566 3.74369 3.97002 4.60963 3.63825C5.47557 3.30649 6.42179 3.24515 7.32333 3.46233C8.22487 3.67951 9.03932 4.165 9.65914 4.85469C9.7518 4.9408 9.87366 4.98859 10.0002 4.98843C10.1267 4.98828 10.2484 4.94019 10.3409 4.85385"
        {...roundStroke}
      />
    </Glyph>
  );
}

export function PillIcon(props: IconProps): ReactNode {
  return (
    <Glyph {...props} viewBox="0 0 20 20">
      <path
        d="M6.76812 6.76812L13.2319 13.2319M3.53509 16.4649C1.4883 14.4181 1.4883 11.0994 3.53509 9.05375L9.05375 3.53509C11.0994 1.4883 14.4181 1.4883 16.4649 3.53509C18.5117 5.58187 18.5117 8.90061 16.4649 10.9463L10.9463 16.4649C8.90061 18.5117 5.58187 18.5117 3.53509 16.4649Z"
        {...stroke}
      />
    </Glyph>
  );
}

export function ReceiptIcon(props: IconProps): ReactNode {
  return (
    <Glyph {...props} viewBox="0 0 20 20">
      <path
        d="M5.33333 11.1034H5.34667M10 11.1034H10.0133M14.6667 11.1034H14.68M5.33333 14.6897H5.34667M10 14.6897H10.0133M14.6667 14.6897H14.68M2 2H18V18H2V2ZM5.33333 4.75862H14.6667V8.06897H5.33333V4.75862Z"
        {...stroke}
      />
    </Glyph>
  );
}

export function UserCogIcon(props: IconProps): ReactNode {
  return (
    <Glyph {...props} viewBox="0 0 20 20">
      <path
        d="M8.33267 12.5H4.99933C4.11528 12.5 3.26743 12.8512 2.64231 13.4763C2.01719 14.1014 1.666 14.9493 1.666 15.8333V17.5M11.9199 13.7754L12.6891 13.457M12.6891 11.5431L11.9199 11.224M14.0425 10.1897L13.7233 9.42057M14.0425 14.8104L13.7233 15.5804M15.9564 10.1897L16.2755 9.42057M16.2747 15.5804L15.9564 14.8104M17.3097 11.5431L18.0797 11.224M17.3097 13.457L18.0797 13.7762M17.4993 12.5C17.4993 13.8807 16.38 15 14.9993 15C13.6186 15 12.4993 13.8807 12.4993 12.5C12.4993 11.1193 13.6186 10 14.9993 10C16.38 10 17.4993 11.1193 17.4993 12.5ZM10.8327 5.83333C10.8327 7.67428 9.34028 9.16667 7.49933 9.16667C5.65838 9.16667 4.166 7.67428 4.166 5.83333C4.166 3.99238 5.65838 2.5 7.49933 2.5C9.34028 2.5 10.8327 3.99238 10.8327 5.83333Z"
        {...roundStroke}
      />
    </Glyph>
  );
}

export function ShieldCheckIcon(props: IconProps): ReactNode {
  return (
    <Glyph {...props} viewBox="0 0 28 28">
      <path
        d="M10.5003 13.9994L12.8334 16.3329L17.4996 11.666M23.3324 15.1667C23.3324 21.0002 19.2495 23.917 14.3966 25.6087C14.1425 25.6948 13.8665 25.6907 13.615 25.597C8.75052 23.917 4.6676 21.0002 4.6676 15.1667V6.99974C4.6676 6.69031 4.7905 6.39356 5.00927 6.17476C5.22805 5.95596 5.52476 5.83304 5.83415 5.83304C8.16725 5.83304 11.0836 4.43299 13.1134 2.6596C13.3606 2.44843 13.6749 2.3324 14 2.3324C14.3251 2.3324 14.6394 2.44843 14.8866 2.6596C16.928 4.44466 19.8327 5.83304 22.1658 5.83304C22.4752 5.83304 22.772 5.95596 22.9907 6.17476C23.2095 6.39356 23.3324 6.69031 23.3324 6.99974V15.1667Z"
        {...roundStroke}
      />
    </Glyph>
  );
}

export function AlertCircleIcon(props: IconProps): ReactNode {
  return (
    <Glyph {...props} viewBox="0 0 18 18">
      <path
        d="M9 5.99976V9M9 12.0002H9.0075M16.5006 9C16.5006 13.1425 13.1425 16.5006 9 16.5006C4.85753 16.5006 1.4994 13.1425 1.4994 9C1.4994 4.85753 4.85753 1.4994 9 1.4994C13.1425 1.4994 16.5006 4.85753 16.5006 9Z"
        {...roundStroke}
      />
    </Glyph>
  );
}

export function ArrowRightIcon(props: IconProps): ReactNode {
  return (
    <Glyph {...props} viewBox="0 0 16 16">
      <path d="M3.3328 8H12.6672M8 12.6672L12.6672 8L8 3.3328" {...roundStroke} />
    </Glyph>
  );
}

export function CalendarCheckIcon(props: IconProps): ReactNode {
  return (
    <Glyph {...props} viewBox="0 0 24 24">
      <path
        d="M8 1.9992V5.99952M16 1.9992V5.99952M3 9.99984H21M9 16.0003L11 18.0005L15 14.0002M5 3.99936H19C20.1046 3.99936 21 4.89486 21 5.99952V20.0006C21 21.1053 20.1046 22.0008 19 22.0008H5C3.89543 22.0008 3 21.1053 3 20.0006V5.99952C3 4.89486 3.89543 3.99936 5 3.99936Z"
        {...roundStroke}
      />
    </Glyph>
  );
}

export function SearchIcon(props: IconProps): ReactNode {
  return (
    <Glyph {...props} viewBox="0 0 20 20">
      <path
        d="M17.5001 17.5001L13.8835 13.8835M15.8333 9.16667C15.8333 12.8486 12.8486 15.8333 9.16667 15.8333C5.48477 15.8333 2.5 12.8486 2.5 9.16667C2.5 5.48477 5.48477 2.5 9.16667 2.5C12.8486 2.5 15.8333 5.48477 15.8333 9.16667Z"
        {...roundStroke}
      />
    </Glyph>
  );
}

export function MessageCircleIcon(props: IconProps): ReactNode {
  return (
    <Glyph {...props} viewBox="0 0 24 24">
      <path
        d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z"
        {...roundStroke}
        strokeLinejoin="round"
      />
    </Glyph>
  );
}
